import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";

import Context from "../context";
import DivSpinner from "../../../components/divSpinner";
import { truncateText, convertDateToUKFormat } from "../../../utils";
import ColumnSort from "./columnSort";

const TABLE_HEADERS = [
    "Job ID",
    "Tradesperson",
    "Customer",
    "Description",
    "Address",
    "Postcode",
    "Service Categories",
];

const Table = () => {
    const { state } = useContext(Context);
    const navigate = useNavigate();

    const tableHeight = useMemo(
        () => ({ height: `${window.innerHeight - 387}px` }),
        []
    );

    if (state.is_loading) {
        return (
            <div style={tableHeight}>
                <DivSpinner />
            </div>
        );
    }

    return (
        <div className="table-responsive p-0 m-3">
            <table className="table align-items-center mb-0">
                <thead>
                    <tr>
                        {TABLE_HEADERS.map((text) => (
                            <th
                                key={text}
                                className="text-uppercase text-secondary text-sm font-weight-bolder opacity-7"
                            >
                                <ColumnSort sortKeyName={text} />
                            </th>
                        ))}
                        <th className="text-center opacity-7"></th>
                        <th className="text-center opacity-7"></th>
                    </tr>
                </thead>

                <tbody>
                    {state.jobs_list.length === 0 ? (
                        <tr>
                            <td colSpan="8" className="text-center">
                                Sorry, no data found!
                            </td>
                        </tr>
                    ) : (
                        state.jobs_list.map((job) => (
                            <tr
                                key={job.job_id}
                                className="hover-row"
                                onClick={() =>
                                    navigate(`/jobs/details/${job.job_id}`)
                                }
                            >
                                {/* Job ID */}
                                <td className="align-middle">
                                    <span className="text-secondary text-sm font-weight-bold">
                                        #{job.job_id}
                                    </span>
                                </td>

                                {/* Tradesperson */}
                                <td>
                                    <div className="d-flex px-2 py-1">
                                        <img
                                            src={job?.ftm?.ftm_profile_image_path}
                                            className="avatar avatar-sm me-3"
                                            alt="avatar"
                                        />
                                        <div className="d-flex flex-column justify-content-center">
                                            <h6 className="mb-0 text-sm">
                                                {job?.ftm?.ftm_forename}{" "}
                                                {job?.ftm?.ftm_surname}
                                            </h6>
                                            <span className="text-xs text-secondary">
                                                Created{" "}
                                                {convertDateToUKFormat(
                                                    job?.job_createdAt
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </td>

                                {/* Customer */}
                                <td className="align-middle">
                                    <span className="text-secondary text-sm font-weight-bold">
                                        {job?.customer?.customer_full_name ??
                                            "N/A"}
                                    </span>
                                </td>

                                {/* Description */}
                                <td className="align-middle">
                                    <span className="text-secondary text-sm font-weight-bold">
                                        {truncateText(
                                            job?.job_description,
                                            20
                                        )}
                                    </span>
                                </td>

                                {/* Address */}
                                <td className="align-middle">
                                    <span className="text-secondary text-sm font-weight-bold">
                                        {job?.job_address
                                            ? truncateText(
                                                  job.job_address,
                                                  20
                                              )
                                            : "N/A"}
                                    </span>
                                </td>

                                {/* Postcode */}
                                <td className="align-middle">
                                    <span className="text-secondary text-sm font-weight-bold">
                                        {job?.job_postcode ?? "N/A"}
                                    </span>
                                </td>

                                {/* Service Category */}
                                <td>
                                    <div className="d-flex align-items-center">
                                        <div className="pe-3 d-flex align-items-center justify-content-center">
                                            <i
                                                className="material-icons text-success"
                                                style={{ fontSize: 30 }}
                                            >
                                                {
                                                    job?.sub_skill?.skill
                                                        ?.skill_web_app_icon
                                                }
                                            </i>
                                        </div>
                                        <div>
                                            <h6 className="mb-1 text-sm">
                                                {
                                                    truncateText(job?.sub_skill?.sub_skill_name, 20)
                                                }
                                            </h6>
                                            <span className="text-xs text-secondary">
                                                {
                                                    job?.sub_skill?.skill
                                                        ?.skill_name
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </td>

                                {/* Certificates */}
                                <td className="text-center align-middle">
                                    {job?.has_job_certificates ? (
                                        <i
                                            className="material-icons text-warning"
                                            style={{ fontSize: 30 }}
                                        >
                                            card_membership
                                        </i>
                                    ) : (
                                        "N/A"
                                    )}
                                </td>

                                {/* Status */}
                                <td className="text-center align-middle">
                                    <Tooltip
                                        title={
                                            job?.job_status_text
                                                ?.job_status_name
                                        }
                                        arrow
                                    >
                                        <i
                                            className="material-icons"
                                            style={{
                                                fontSize: 30,
                                                color: job?.job_status_text
                                                    ?.job_status_color,
                                            }}
                                        >
                                            {
                                                job?.job_status_text
                                                    ?.job_status_icon
                                            }
                                        </i>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default Table;